import React, { FC, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';

import AuthDivider from 'components/Auth/AuthDivider';
import AuthLayout from 'components/Auth/AuthLayout';
import GoogleSignInButton from 'components/Auth/GoogleSignInButton';
import OtpAuthForm, { AuthHandoffState } from 'components/Auth/OtpAuthForm';
import PasswordSignUpForm from 'components/Auth/PasswordSignUpForm';
import { PASSWORD_LOGIN_ENABLED } from 'utils/featureFlags';

export interface PageSignUpProps {
    className?: string;
}

const PageSignUp: FC<PageSignUpProps> = ({ className = '' }) => {
    // Set when somebody bounced here from /login because there is no account
    // for the address they tried. Saves them typing it a second time.
    const location = useLocation();
    const handoffEmail = (location.state as AuthHandoffState | null)?.email ?? '';

    // Password registration is off by default now (PASSWORD_LOGIN_ENABLED):
    // handing somebody a password they would not be allowed to sign in with is
    // worse than not offering one. Kept behind the flag, like the login form.
    const [showPasswordForm, setShowPasswordForm] = useState<boolean>(false);

    return (
        <div className={`nc-PageSignUp  ${className}`} data-nc-id="PageSignUp">
            <Helmet>
                <title>Sign up | Instaacard</title>
            </Helmet>
            <AuthLayout
                title="Create your account"
                subtitle="Build your digital business card and start sharing it in minutes."
                footer={
                    <>
                        Already have an account? {` `}
                        <Link
                            className="font-medium text-green-600 hover:underline"
                            to="/login"
                            // Only worth carrying when we were handed one; an
                            // empty state entry is just noise in the history.
                            state={handoffEmail ? ({ email: handoffEmail } as AuthHandoffState) : undefined}
                        >
                            Sign in
                        </Link>
                    </>
                }
            >
                <GoogleSignInButton text="signup_with" />

                <AuthDivider />

                {/*
                 * Same three screens as /login plus a profile step: the
                 * request carries `purpose: "signup"`, so an address that
                 * already has an account is turned away here with a link
                 * back to /login rather than quietly signed in.
                 */}
                <OtpAuthForm mode="signup" defaultEmail={handoffEmail} />

                {PASSWORD_LOGIN_ENABLED && (
                    <div className="space-y-6">
                        <AuthDivider />

                        {showPasswordForm ? (
                            <PasswordSignUpForm />
                        ) : (
                            <button
                                type="button"
                                className="block w-full text-center text-sm text-green-600 hover:underline"
                                onClick={() => setShowPasswordForm(true)}
                            >
                                Create an account with a password instead
                            </button>
                        )}
                    </div>
                )}
            </AuthLayout>
        </div>
    );
};

export default PageSignUp;
